import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Home() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-black overflow-x-hidden pb-20">
      {/* Animated background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20"></div>
        <div className="absolute top-0 left-1/4 w-64 h-64 md:w-96 md:h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-24 right-1/4 w-64 h-64 md:w-96 md:h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-8">
        <div className="max-w-5xl w-full">
          
          {/* Hero Section */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 md:gap-3 mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl md:rounded-2xl flex items-center justify-center">
                <svg className="w-7 h-7 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight">AIIA</h1>
            </div>
            
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
              Where Everyone <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Profits</span>
            </h2>
            
            <p className="text-base md:text-xl text-gray-300 mb-3 max-w-2xl mx-auto leading-relaxed">
              The revolutionary social platform that rewards creators, commenters, and communities alike.
            </p>
            
            <p className="text-sm md:text-lg text-gray-400 max-w-xl mx-auto">
              TikTok-style videos + Discord communities + Fair revenue sharing = The future of social media
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-16">
            <div className="group bg-gradient-to-br from-purple-900/20 to-purple-900/5 backdrop-blur-sm border border-purple-500/20 rounded-2xl md:rounded-3xl p-5 md:p-8 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-purple-500/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-purple-500/30 transition-colors">
                <svg className="w-5 h-5 md:w-8 md:h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-2xl font-bold text-white mb-2 md:mb-3">Viral Content</h3>
              <p className="text-sm md:text-base text-gray-400 leading-relaxed">Share 30-second videos, audio messages, and projects with the world</p>
            </div>

            <div className="group bg-gradient-to-br from-pink-900/20 to-pink-900/5 backdrop-blur-sm border border-pink-500/20 rounded-2xl md:rounded-3xl p-5 md:p-8 hover:border-pink-500/40 transition-all duration-300 hover:scale-105">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-pink-500/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-pink-500/30 transition-colors">
                <svg className="w-5 h-5 md:w-8 md:h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-2xl font-bold text-white mb-2 md:mb-3">Communities</h3>
              <p className="text-sm md:text-base text-gray-400 leading-relaxed">Build Discord-style spaces with channels, roles, and real-time chat</p>
            </div>

            <div className="group bg-gradient-to-br from-green-900/20 to-green-900/5 backdrop-blur-sm border border-green-500/20 rounded-2xl md:rounded-3xl p-5 md:p-8 hover:border-green-500/40 transition-all duration-300 hover:scale-105">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-green-500/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-green-500/30 transition-colors">
                <svg className="w-5 h-5 md:w-8 md:h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-2xl font-bold text-white mb-2 md:mb-3">Revenue Sharing</h3>
              <p className="text-sm md:text-base text-gray-400 leading-relaxed">Comments earn money. Moderators get paid. Communities profit together.</p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            {!isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/signup"
                  className="group relative px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full overflow-hidden shadow-2xl shadow-purple-500/50 hover:shadow-purple-500/80 transition-all duration-300 hover:scale-105"
                >
                  <span className="relative z-10">Start Creating Free</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
                
                <Link 
                  to="/login"
                  className="px-10 py-5 bg-white/5 backdrop-blur-sm text-white text-lg font-bold rounded-full border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300"
                >
                  Sign In
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/feed"
                  className="group relative px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full overflow-hidden shadow-2xl shadow-purple-500/50 hover:shadow-purple-500/80 transition-all duration-300 hover:scale-105"
                >
                  <span className="relative z-10">Go to Feed</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
                
                <Link 
                  to="/upload"
                  className="px-10 py-5 bg-white/5 backdrop-blur-sm text-white text-lg font-bold rounded-full border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300"
                >
                  Upload Post
                </Link>
              </div>
            )}

            <p className="text-gray-500 text-xs md:text-sm mt-6">Join 1,248+ creators already earning</p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-10 md:mt-16 pt-8 md:pt-12 border-t border-white/10">
            <div className="text-center">
              <div className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-1">1,248</div>
              <div className="text-xs md:text-sm text-gray-400 font-medium">Videos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-1">94%</div>
              <div className="text-xs md:text-sm text-gray-400 font-medium">AI Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-1">$12.5K</div>
              <div className="text-xs md:text-sm text-gray-400 font-medium">Paid Out</div>
            </div>
          </div>

          {/* How It Works - Hidden on mobile when logged in */}
          <div className="hidden md:block mt-16 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12">
            <h3 className="text-3xl font-bold text-white text-center mb-12">How Revenue Sharing Works</h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-purple-500/30">
                  <span className="text-2xl font-bold text-purple-400">1</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Creators Earn</h4>
                <p className="text-gray-400">Your content generates revenue from views, likes, and premium subscriptions</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-pink-500/30">
                  <span className="text-2xl font-bold text-pink-400">2</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Comments Get Paid</h4>
                <p className="text-gray-400">Engaging comments automatically earn based on likes and engagement</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-green-500/30">
                  <span className="text-2xl font-bold text-green-400">3</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">Communities Profit</h4>
                <p className="text-gray-400">Moderators and active members share in community success</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
